<template>
  <!-- Description and Related Monuments tab for Monument -->
  <div class="monument-description">
    <!-- Monument image(s) -->
    <div
      v-if="currentEntry.images.length > 0"
      class="monument-description-photos description"
    >
      <!-- Big image -->
      <div class="main-photo">
        <div class="main-photo-container" @click="showLightbox = true">
          <img :src="mainImage.url" />
        </div>
      </div>
      <div
        class="main-photo-description"
        v-html="`${mainImage.caption} ${mainImage.copyright}`"
      ></div>
      <!-- Image thumbnails -->
      <div class="photo-thumbnails-wrapper">
        <div
          class="thumbnails-arrows-container"
          ref="thumbnailsArrowsContainer"
          v-if="imageScroll"
        >
          <button
            ref="thumbnailLeftArrow"
            class="thumbnail-arrow-button"
            @click="scrollRight()"
          >
            <font-awesome-icon
              :icon="['fas', 'chevron-left']"
              class="thumbnail-arrow"
            />
          </button>
          <button
            ref="thumbnailRightArrow"
            class="thumbnail-arrow-button"
            @click="scrollLeft()"
          >
            <font-awesome-icon
              :icon="['fas', 'chevron-right']"
              class="thumbnail-arrow"
            />
          </button>
        </div>
        <div class="thumbnails-padding">
          <div
            class="photo-thumbnails-container"
            ref="photoThumbnailsContainer"
            :class="{ 'no-scroll': !imageScroll }"
          >
            <div
              class="photo-thumbnail"
              v-for="image in currentEntry.images"
              :key="image.url"
              ref="thumbnail"
              :class="{ currentImage: imageMatches(image.url) }"
            >
              <img @click="changeMainImage(image)" :src="image.urlThumbnail" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- If no images are available -->
    <div class="no-monument-images" v-else>
      <span
        >No image available. If you would like to submit any image(s) for this
        Monument, please contact
        <a href="mailto:office@museumwnf.net">office@museumwnf.net</a>.
      </span>
    </div>
    <!-- Whole-page lightbox for photo slideshow/full-size viewing -->
    <div
      class="lightbox-container"
      v-if="showLightbox"
      @click="showLightbox = false"
    >
      <div class="lightbox">
        <img :src="mainImage.url" />
      </div>
      <!-- Slideshow controls -->
      <div
        class="lightbox-left lightbox-control"
        v-if="currentEntry.images.length > 1"
      >
        <button @click.stop="slideshow('left')">
          <font-awesome-icon :icon="['fas', 'chevron-circle-left']" />
        </button>
      </div>
      <div
        class="lightbox-right lightbox-control"
        v-if="currentEntry.images.length > 1"
      >
        <button @click.stop="slideshow('right')">
          <font-awesome-icon :icon="['fas', 'chevron-circle-right']" />
        </button>
      </div>
    </div>
    <!-- Display language selection if available -->
    <div
      class="monument-language-selection description"
      v-if="languagesList.length > 1"
    >
      <label>Read in </label>
      <select v-model="defaultLanguage">
        <option v-for="language in languagesList" :key="language">
          {{ language }}
        </option>
      </select>
    </div>
    <div class="monument-name description">
      <div v-html="currentDetails.name" :class="{ rtl: rtl }"></div>
      <div
        v-if="containsText(currentDetails.alsoKnownAs)"
        :class="{ rtl: rtl }"
      >
        <span>Also known as: </span>
        <span v-html="currentDetails.alsoKnownAs"></span>
      </div>
    </div>
    <div
      class="monument-date description"
      v-if="currentDetails.date"
      :class="{ rtl: rtl }"
    >
      Date of Monument: <span v-html="currentDetails.date"></span>
    </div>
    <div
      class="monument-artist description"
      v-if="currentDetails.artist"
      :class="{ rtl: rtl }"
    >
      Artist: <span v-html="currentDetails.artist"></span>
    </div>
    <div class="monument-history-text description">
      <div
        class="monument-history-label"
        :class="{ rtl: rtl }"
        v-if="containsText(currentDetails.history)"
      >
        History
      </div>
      <DescriptionComponent
        v-if="containsText(currentDetails.history)"
        :language="defaultLanguage"
        :rtl="rtl"
        :texts="textsArray(currentEntry.details, 'history')"
        :characters="historyChars"
        :glossary="glossary"
      >
      </DescriptionComponent>
    </div>
    <div class="monument-description-text description">
      <div
        class="monument-description-label"
        :class="{ rtl: rtl }"
        v-if="containsText(currentDetails.history)"
      >
        Description
      </div>
      <DescriptionComponent
        :language="defaultLanguage"
        :rtl="rtl"
        :texts="textsArray(currentEntry.details, 'description')"
        :characters="descriptionChars"
        :glossary="glossary"
      >
      </DescriptionComponent>
    </div>
    <div class="monument-citation-container description" :class="{ rtl: rtl }">
      <div class="monument-authors">
        <div v-if="currentDetails.authors">
          <span class="citation-label">Authors: </span>
          <span v-html="currentDetails.authors"></span>
        </div>
        <div v-if="currentDetails.preparedBy">
          Prepared by: <span class="info-authors-name"
            :class="{ 'cv-hover': currentDetails.authorscv }"
            @mouseover="authorHover = currentDetails.preparedBy,
              positionHover($event, currentDetails.preparedBy)" 
            @mouseleave="authorHover = null"
            v-html="currentDetails.preparedBy"></span>
        </div>
        <div v-if="currentDetails.copyEditedBy">
          Copyedited by: <span class="info-authors-name"
            :class="{ 'cv-hover': currentDetails.authorscv }"
            @mouseover="authorHover = currentDetails.copyEditedBy,
              positionHover($event, currentDetails.copyEditedBy)" 
            @mouseleave="authorHover = null"
            v-html="currentDetails.copyEditedBy" ></span>
        </div>
        <div v-if="currentDetails.translationBy">
          Translation by: <span class="info-authors-name"
            :class="{ 'cv-hover': currentDetails.authorscv }"
            @mouseover="authorHover = currentDetails.translationBy,
              positionHover($event, currentDetails.translationBy)" 
            @mouseleave="authorHover = null"
            v-html="currentDetails.translationBy"></span>
        </div>
        <div v-if="currentDetails.translationCopyEditedBy">
          Translation copyedited by:
          <span class="info-authors-name"
            :class="{ 'cv-hover': currentDetails.authorscv }"
            @mouseover="authorHover = currentDetails.translationCopyEditedBy,
              positionHover($event, currentDetails.translationCopyEditedBy)" 
            @mouseleave="authorHover = null"
            v-html="currentDetails.translationCopyEditedBy"></span>
        </div>
        <div v-if="!lessThan724">
          <div class="info-authors-cv"
            v-for="author in currentDetails.authorscv" :key="author.name"
            v-show="author.curriculum && authorHover === author.name"
            v-html="author.curriculum">
          </div>
        </div>
      </div>

      <!-- <div class="info-authors-name"
        :class="{ 'cv-hover': currentDetails.authorscv }"
        v-for="author in currentDetails.authorscv" :key="author.name"
        @mouseover="authorHover = author.name
          positionHover($event)" 
        @mouseleave="authorHover = null">
        {{ author.name }}
        <div class="info-authors-cv" 
          v-show="author.curriculum && authorHover === author.name"
          v-html="author.curriculum">
        </div>
      </div> -->

      <div class="monument-bibliography" v-if="currentDetails.bibliography">
        <span class="citation-label">Bibliography: </span>
        <span v-html="currentDetails.bibliography"></span>
      </div>
      <div v-if="currentDetails.citation">
        <span class="citation-label">Citation: </span>
        <a :href="currentDetails.citation" target="_blank">{{
          currentDetails.citation
        }}</a>
      </div>
    </div>
  </div>
</template>

<script>
import DescriptionComponent from "./DescriptionComponent.vue";
import language from "../mixins/language.js";
import media from "../mixins/media.js";
export default {
  name: "MonumentDescription",
  metaInfo() {
    return {
      // Add meta information to improve search results
      meta: [
        {
          name: "thumbnail",
          content: this.mainImage.url ? this.mainImage.url : "",
        },
      ],
    };
  },
  props: ["entry", "language", "location", "country", "glossary"],
  components: {
    DescriptionComponent: DescriptionComponent,
  },
  mixins: [language, media],
  data() {
    return {
      rtl: false,
      defaultLanguage: this.language,
      historyChars: 550,
      descriptionChars: 2000,
      languagesList: [],
      currentEntry: this.entry,
      mainImage: {
        url: "",
        caption: "",
        copyright: "",
      },
      showLightbox: false,

      authorHover: "",
      authorCurriculum: "",

      lessThan1199: window.matchMedia("(max-width: 1199px)").matches,
      lessThan1099: window.matchMedia("(max-width: 1099px)").matches,
      lessThan899: window.matchMedia("(max-width: 899px").matches,
      lessThan724: window.matchMedia("(max-width: 724px)").matches,
      lessThan599: window.matchMedia("(max-width: 599px)").matches,
    };
  },
  computed: {
    currentDetails: function () {
      return this.currentEntry.details[
        this.getLanguageIndex(this.currentEntry.details, this.defaultLanguage)
      ];
    },
    // Determine scroll display for thumbnails
    // Based on # of thumbnails & width of page
    imageScroll: function () {
      let length = this.currentEntry.images.length;
      if (this.lessThan599 && length > 2) {
        return true;
      } else if (this.lessThan724 && length > 3) {
        return true;
      } else if (this.lessThan899 && length > 4) {
        return true;
      } else if (this.lessThan1099 && length > 6) {
        return true;
      } else if (this.lessThan1199 && length > 8) {
        return true;
      } else if (!this.lessThan1199 && length > 6) {
        return true;
      }
      return false;
    },
  },
  watch: {
    entry() {
      this.currentEntry = this.entry;
      this.getLanguageList(this.currentEntry.details);
      this.setLanguage(this.languagesList);
      this.checkRTL();
      this.setMainImage(this.currentEntry.images);
    },
    defaultLanguage() {
      this.checkRTL();
      if (this.defaultLanguage !== this.$route.params.language) { 
        this.$router.push({ params: { language: `lan-${this.defaultLanguage}` } });
      }
    },
  },
  methods: {
    checkRTL: function () {
      if (this.defaultLanguage === "ar") {
        this.rtl = true;
      } else {
        this.rtl = false;
      }
    },
    setLanguage: function (array) {
      let language = false;
      let english = false;
      // Check to see if current language is available
      for (let i = 0; i < array.length; i++) {
        if (array[i] === this.defaultLanguage) {
          language = true;
          break;
        }
      }
      // If language is true, no change required, exit
      if (language) {
        return;
        // If previously selected language is not available,
        // find English or first available language
      } else {
        for (let i = 0; i < array.length; i++) {
          // If English is available, set default
          if (array[i] === "en") {
            english = true;
            this.defaultLanguage = "en";
            break;
          }
        }
        // Otherwise, set to first available language
        if (!english) {
          this.defaultLanguage = array[0].langId;
        }
      }
    },
    // Function for photo slideshow
    slideshow: function (clicked) {
      let urls = [];
      for (let i = 0; i < this.currentEntry.images.length; i++) {
        urls.push(this.currentEntry.images[i].url);
      }
      let index = urls.indexOf(this.mainImage.url);
      let newIndex;
      if (clicked === "right") {
        if (index === urls.length - 1) {
          newIndex = 0;
        } else {
          newIndex = index + 1;
        }
      } else if (clicked === "left") {
        if (index === 0) {
          newIndex = urls.length - 1;
        } else {
          newIndex = index - 1;
        }
      }
      this.mainImage.url = urls[newIndex];
      let imageInfo = this.currentEntry.images[newIndex].imageInfo[0];
      if (this.containsText(imageInfo.caption)) {
        this.mainImage.caption = imageInfo.caption;
      } else {
        let location = this.returnValueInLanguage(
          this.defaultLanguage,
          this.location
        ).value;
        let country = this.returnValueInLanguage(
          this.defaultLanguage,
          this.country
        ).value;
        let caption = `${this.currentDetails.name} (${location}, ${country})`;
        this.mainImage.caption = caption;
      }
      if (this.containsText(imageInfo.photographer)) {
        this.mainImage.caption += ` | Photographer: ${imageInfo.photographer}`;
      }
      if (this.containsText(imageInfo.copyright)) {
        this.mainImage.copyright = ` | © ${imageInfo.copyright}`;
      } else {
        this.mainImage.copyright = " | © Museum With No Frontiers (MWNF)";
      }
    },
    imageMatches: function (src) {
      if (src === this.mainImage.url) {
        return true;
      } else {
        return false;
      }
    },
    changeMainImage: function (image) {
      this.mainImage.url = image.url;
      if (this.containsText(image.imageInfo[0].caption)) {
        this.mainImage.caption = image.imageInfo[0].caption;
      } else {
        // let location = this.returnValueInLanguage(
        //   this.defaultLanguage,
        //   this.location
        // ).value;
        // let country = this.returnValueInLanguage(
        //   this.defaultLanguage,
        //   this.country
        // ).value;
        let location = this.returnValueInLanguage("en", this.location).value;
        let country = this.returnValueInLanguage("en", this.country).value;
        let caption = `${this.currentDetails.name} (${location}, ${country})`;
        this.mainImage.caption = caption;
      }
      if (this.containsText(image.imageInfo[0].photographer)) {
        this.mainImage.caption += ` | Photographer: ${image.imageInfo[0].photographer}`;
      }
      if (this.containsText(image.imageInfo[0].copyright)) {
        this.mainImage.copyright = ` | © ${image.imageInfo[0].copyright}`;
      } else {
        this.mainImage.copyright = "| © Museum With No Frontiers (MWNF)";
      }
    },
    scrollLeft: function () {
      let thumbnailWidth = this.$refs.thumbnail[0].clientWidth + 50;
      let current = this.$refs.photoThumbnailsContainer.scrollLeft;
      this.$refs.photoThumbnailsContainer.scrollTo({
        left: current + thumbnailWidth,
        behavior: "smooth",
      });
    },
    scrollRight: function () {
      let thumbnailWidth = this.$refs.thumbnail[0].clientWidth + 50;
      let current = this.$refs.photoThumbnailsContainer.scrollLeft;
      this.$refs.photoThumbnailsContainer.scrollTo({
        left: current - thumbnailWidth,
        behavior: "smooth",
      });
    },
    getLanguageIndex: function (array, language) {
      for (let i = 0; i < array.length; i++) {
        if (array[i].langId === language) {
          return i;
        }
      }
      // set index to default of "en" if language isn't available
      for (let j = 0; j < array.length; j++) {
        if (array[j].langId === "en") {
          return j;
        }
      }
    },
    setMainImage: function (images) {
      if (images.length > 0) {
        let index = this.getLanguageIndex(
          images[0].imageInfo,
          this.defaultLanguage
        );
        this.mainImage.url = images[0].url;
        if (this.containsText(images[0].imageInfo[index].caption)) {
          this.mainImage.caption = images[0].imageInfo[index].caption;
        } else {
          // Set location and country to English only (can't guarantee availability)
          let location = this.returnValueInLanguage("en", this.location).value;
          let country = this.returnValueInLanguage("en", this.country).value;
          let caption = `${this.currentDetails.name} (${location}, ${country})`;
          this.mainImage.caption = caption;
        }
        if (this.containsText(images[0].imageInfo[index].photographer)) {
          this.mainImage.caption += ` | Photographer: ${images[0].imageInfo[index].photographer}`;
        }
        if (this.containsText(images[0].imageInfo[index].copyright)) {
          this.mainImage.copyright = ` | © ${images[0].imageInfo[index].copyright}`;
        } else {
          this.mainImage.copyright = " | © Museum With No Frontiers (MWNF)";
        }
      } else {
        this.mainImage.url = "";
        this.mainImage.caption = "";
        this.mainImage.copyright = "";
      }
    },
    getLanguageList: function (array) {
      let languages = [];
      for (let i = 0; i < array.length; i++) {
        languages.push(array[i].langId);
      }
      this.languagesList = languages;
    },
    containsText: function (value) {
      if (value && value.length > 0) {
        return true;
      } else {
        return false;
      }
    },
    textsArray: function (arr, prop) {
      let array = [];
      for (let i = 0; i < arr.length; i++) {
        let obj = {};
        obj.langId = arr[i].langId;
        // If there is only one language (English)
        if (arr.length === 1) {
          // AND there is no description text entered
          if (!arr[0][prop] || arr[0][prop].length === 0) {
            obj.description = "The Description is not yet available.";
          } else {
            obj.description = arr[i][prop];
          }
          // If there is more than one language
        } else if (obj.langId === "en") {
          // BUT English description is empty
          // there should be other descriptions available
          if (!arr[i][prop] || arr[i][prop].length === 0) {
            obj.description =
              "The Description is not available in English. Please select available languages from the dropdown menu.";
          } else {
            obj.description = arr[i][prop];
          }
        } else {
          obj.description = arr[i][prop];
        }
        array.push(obj);
      }
      return array;
    },
    positionHover: function(e, name) {
      if (!this.currentDetails.authorscv || this.lessThan724) {
        return;
      }
      this.setAuthorCurriculum(name);
      let author = e.target.offsetTop;
      let cvs = e.target.parentNode.parentNode.lastChild.getElementsByClassName("info-authors-cv");
      let index;
      for (let i = 0; i < cvs.length; i++) {
        if (cvs[i].innerText === this.authorCurriculum) {
          index = i;
          break;
        }
      }
      cvs[index].style.display = "block";
      let height = cvs[index].offsetHeight;
      cvs[index].style.display = "none";
      cvs[index].style.top = (author - height - 10) + "px";
      // cvs[0].style.display = "block";
      // let height = cvs[0].offsetHeight;
      // cvs[0].style.display = "none";
      // cvs[0].style.top = (author - height - 10) + "px";
    },
    setAuthorCurriculum: function(name) {
      let authorscv = this.currentDetails.authorscv;
      for (let i = 0; i < authorscv.length; i++) {
        if (name === authorscv[i].name) {
          this.authorCurriculum = authorscv[i].curriculum;
          break;
        }
      }
    }
  },
  mounted() {
    // console.log(this.currentEntry);
    if (this.currentEntry.images.length > 0) {
      this.setMainImage(this.currentEntry.images);
    }
    this.getLanguageList(this.currentEntry.details);
    this.checkRTL();
  },
};
</script>

<style lang="scss">
.monument-description-photos {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
}
.main-photo {
  height: 550px;
  width: 100%;
  background-color: $light-grey;
}
.main-photo-container {
  height: 100%;
  width: 100%;
}
.main-photo img {
  @include image-scale;
  object-fit: contain;
}
.main-photo-description {
  @include flex-center;
  min-height: 50px;
  width: 100%;
  color: $white;
  background-color: $dark-grey;
  margin-bottom: 20px;
  text-align: center;
  padding: 10px;
}
.photo-thumbnails-wrapper {
  position: relative;
  height: 120px;
  min-width: 100%;
}
.thumbnails-arrows-container {
  @include flex-center;
  justify-content: space-between;
  position: absolute;
  height: 100%;
  width: 100%;
}
.thumbnail-arrow-button {
  @include button-styles;
  height: 100%;
  width: 28px;
  color: $white;
  background-color: $medium-dark-grey;
  z-index: 1;
}
.thumbnails-padding {
  @include flex-center;
  height: 100%;
  width: 100%;
  padding: 0 40px;
}
.photo-thumbnails-container {
  @include flex-center;
  justify-content: flex-start;
  height: 100%;
  width: 100%;
  overflow: hidden;
}
.no-scroll {
  justify-content: center;
}
.photo-thumbnail {
  min-height: 120px;
  height: 120px;
  min-width: 120px;
  width: 120px;
  margin: 0 10px;
  z-index: 0;
}
.photo-thumbnail:first-child {
  margin-left: 0;
}
.photo-thumbnail img {
  @include image-scale;
  object-fit: cover;
}
.currentImage {
  border: 6px solid $dark-green;
}
.no-monument-images {
  margin-bottom: 20px;
}
.lightbox-container {
  @include flex-center;
  height: 100vh;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 200;
  background-color: rgba(black, 0.9);
}
.lightbox {
  height: 90%;
  width: 90%;
}
.lightbox img {
  @include image-scale;
  object-fit: contain;
}
.lightbox-control {
  position: absolute;
  font-size: 40px;
}
.lightbox-control button {
  @include flex-center;
  @include button-styles;
  height: 45px;
  width: 45px;
  color: rgba(white, 0.2);
}
.lightbox-left {
  left: 10px;
}
.lightbox-right {
  right: 10px;
}
.description {
  margin-bottom: 20px;
}
.description,
.monument-name *,
.monument-date *,
.monument-artist *,
.monument-history-text *,
.monument-description-text *,
.monument-citation-container * {
  @include texts;
  @include font-size-6;
}
.monument-history-label,
.monument-description-label {
  margin-bottom: 5px;
}
.description a {
  @include link-styles;
}
.description a:hover {
  @include link-hover-styles;
}

.monument-authors,
.monument-bibliography {
  margin-bottom: 20px;
}
  .info-authors-name {
    // position: relative;
    cursor: default;
  }
    .cv-hover {
      @include link-styles;
      color: $link-blue;
    }
      .cv-hover:hover {
        @include link-hover-styles;
      }
    .info-authors-cv {
      position: absolute;
      left: 50%;
      -webkit-transform: translateX(-50%);
      transform: translateX(-50%);
      width: 500px;
      color: white;
      background-color: $medium-green;
      padding: 20px;
    }
.citation-label {
  font-weight: bold;
}

.rtl {
  direction: rtl;
}

@media only screen and (max-width: 849px) {
  .main-photo {
    height: 475px;
  }
}

@media only screen and (max-width: 649px) {
  .main-photo {
    height: 400px;
  }
}
</style>
